  import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  IconButton,
  Typography,
  CircularProgress
} from '@mui/material';
import {
  CloudOff as CloudOffIcon,
  Cloud as CloudIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import * as api from '../api/api';

function S3StatusAlert({ onStatusChange, showOnSuccess = false, autoCheck = true }) {
  const [s3Status, setS3Status] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const checkS3Status = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get S3 diagnosis
      const s3Data = await api.checkS3Status();
      setS3Status(s3Data);
      
      // Notify parent component of status change
      if (onStatusChange) {
        const isHealthy = s3Data.diagnosis && !s3Data.diagnosis.error;
        const statusUpdate = {
          isHealthy,
          outcomeCount: s3Data.diagnosis?.outcomes_count || 0,
          error: s3Data.diagnosis?.error || s3Data.error
        };
        
        onStatusChange(statusUpdate);
      }
      
    } catch (err) {
      console.error('Error checking S3 status:', err);
      setError(err.message);
      
      if (onStatusChange) {
        const errorStatus = {
          isHealthy: false,
          outcomeCount: 0,
          error: err.message
        };
        
        onStatusChange(errorStatus);
      }
    } finally {
      setLoading(false);
    }
  }, [onStatusChange]);

  useEffect(() => {
    if (autoCheck) {
      checkS3Status();
    }
  }, [autoCheck, checkS3Status]);

  // Don't render anything if we haven't checked yet
  if (!s3Status && !error) return null;
  
  const diagnosis = s3Status?.diagnosis;
  const hasError = error || diagnosis?.error;
  
  // Don't show success alert unless explicitly requested
  if (!hasError && !showOnSuccess) return null;

  const getSeverity = () => {
    return hasError ? 'warning' : 'success';
  };

  const getMessage = () => {
    if (hasError) {
      return 'S3 storage unavailable - using local data';
    }
    return 'S3 storage connected';
  };

  return (
    <Alert 
      severity={getSeverity()} 
      sx={{ mb: 2 }}
      icon={hasError ? <CloudOffIcon /> : <CloudIcon />}
      action={
        <IconButton
          color="inherit"
          size="small"
          onClick={checkS3Status}
          disabled={loading}
          title="Refresh status"
        >
          {loading ? <CircularProgress size={16} /> : <RefreshIcon />}
        </IconButton>
      }
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="body2">
          {getMessage()}
        </Typography>
      </Box>
    </Alert>
  );
}

export default S3StatusAlert;
